import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Lock } from "lucide-react"
import { type FormEvent, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"

import type { Cuenta } from "./tipos"

/**
 * El PIN que abre una cuenta para esta sesión.
 *
 * ⚠ **Se pide una vez por sesión y por cuenta.** Abrir la de gastos no abre la de
 * nómina, y cerrar sesión las vuelve a cerrar todas. Lo decide el servidor
 * (`services/pin_cuentas.py`): esto sólo lo pregunta.
 *
 * ⚠ **Esconder el saldo no es protegerlo.** Mientras la cuenta esté cerrada, el
 * servidor no devuelve ni su saldo, ni su extracto, ni deja pagar desde ella — la
 * pantalla se limita a pedir el PIN cuando recibe `CUENTA_BLOQUEADA`.
 *
 * ⚠ **Cinco intentos.** El sexto espera quince minutos, y la cuenta de intentos es
 * por persona y cuenta: probar el PIN de una no puede dejar bloqueada la otra.
 */
export function DialogoPin({
  cuenta,
  onCerrar,
  onAbierta,
}: {
  /** La cuenta que hay que abrir. `null` cierra el diálogo. */
  cuenta: Cuenta | null
  onCerrar: () => void
  onAbierta?: () => void
}) {
  const clienteQuery = useQueryClient()
  const [pin, setPin] = useState("")

  const abrir = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/cuentas-bancarias/${cuenta?.id}/desbloquear`, { pin: pin.trim() }),
    onSuccess: () => {
      // Todo lo que el servidor negaba mientras estaba cerrada: saldos, extracto y la
      // propuesta de pago.
      clienteQuery.invalidateQueries({ queryKey: ["resumen-cuenta"] })
      clienteQuery.invalidateQueries({ queryKey: ["movimientos"] })
      clienteQuery.invalidateQueries({ queryKey: ["saldo-cuenta"] })
      clienteQuery.invalidateQueries({ queryKey: ["estado-cuenta"] })
      setPin("")
      onAbierta?.()
      onCerrar()
    },
  })

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    if (pin.trim().length >= 4) abrir.mutate()
  }

  const error = abrir.error instanceof ErrorApi ? abrir.error : null

  return (
    <Modal
      abierto={cuenta !== null}
      titulo={cuenta ? `PIN de ${cuenta.nombre}` : "PIN"}
      descripcion="Se pide una vez por sesión. Sin él no se ve el saldo ni se paga desde esta cuenta."
      ancho="sm"
      onCerrar={onCerrar}
    >
      <form onSubmit={enviar} className="space-y-4">
        <label className="block space-y-1 text-sm" htmlFor="pin-cuenta">
          <span className="font-medium">PIN</span>
          <input
            id="pin-cuenta"
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            maxLength={32}
            className="campo w-full tabular-nums"
          />
        </label>
        {error && (
          <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
            {error.message}
            {typeof error.detalles?.restantes === "number" && (
              <> · quedan {String(error.detalles.restantes)} intentos</>
            )}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Boton variante="fantasma" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton
            variante="primario"
            type="submit"
            disabled={abrir.isPending || pin.trim().length < 4}
          >
            <Lock size={16} aria-hidden={true} />
            {abrir.isPending ? "Abriendo…" : "Abrir cuenta"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}
